// A controller is a function (car, world, dt) → {throttle, brake, steerTarget}.
// Physics never knows the difference — keyboard, gamepad, AI, replay
// are all just controllers returning the same input shape. This is the
// seam where the manager game will let the player choose "drive" or
// "watch" before any session.
//
// Future controllers fit the same signature:
//   createAIController({ racingLine, driverModel, otherCars })
//   createGamepadController({ deadzone, bindings })
//   createReplayController({ recordedInputs })
//   createScriptedController({ inputsByTime })

// Module-scoped key state. The keydown/keyup listeners attach to
// `target` (window by default) at attachKeyboard() time and stay until
// detachKeyboard() is called. Multiple keyboard controllers share the
// same key map — there's only one keyboard.
const keys = {};
let attached = false;
let attachedTarget = null;
let onToggleDriveRequested = null;

function handleKeyDown(e) {
  const k = e.key.toLowerCase();
  keys[k] = true;
  if (k === 'm' && onToggleDriveRequested) onToggleDriveRequested();
  if (k === ' ' || k.startsWith('arrow')) e.preventDefault();
}
function handleKeyUp(e) {
  keys[e.key.toLowerCase()] = false;
}

// Attach keyboard listeners. Idempotent — calling twice is a no-op.
// `onToggleDrive` fires on 'M' (caller flips selected car's controller
// type between manual and AI). The reset-to-grid action is still
// available via the sim handle's resetCars() — it just isn't bound
// to a key right now; future UI will trigger it.
export function attachKeyboard(target = window, { onToggleDrive } = {}) {
  if (attached) return;
  attached = true;
  attachedTarget = target;
  onToggleDriveRequested = onToggleDrive ?? null;
  target.addEventListener('keydown', handleKeyDown);
  target.addEventListener('keyup', handleKeyUp);
}

export function detachKeyboard() {
  if (!attached) return;
  attached = false;
  attachedTarget?.removeEventListener('keydown', handleKeyDown);
  attachedTarget?.removeEventListener('keyup', handleKeyUp);
  attachedTarget = null;
  onToggleDriveRequested = null;
  // Clear key state so any "stuck" keys don't carry across mounts.
  for (const k of Object.keys(keys)) delete keys[k];
}

// Returns a controller closure that reads the shared `keys` map
// populated by attachKeyboard listeners.
export function createKeyboardController() {
  return function getInputs(_self) {
    let s = 0;
    if (keys['a'] || keys['arrowleft'])  s -= 1;
    if (keys['d'] || keys['arrowright']) s += 1;
    return {
      throttle:    (keys['w'] || keys['arrowup'])                ? 1 : 0,
      brake:       (keys['s'] || keys['arrowdown'] || keys[' ']) ? 1 : 0,
      steerTarget: s,
    };
  };
}

// AI controller lives in ai-controller.js — kept separate because the
// AI logic will grow (skill model, traffic awareness, replay variant)
// while the keyboard controller stays fixed.
